import moment, { Moment, MomentInput } from 'moment-timezone';
import { Job } from '@mp/graphql/generated/graphql';

export const setDateToTimeMoment = (
  date: MomentInput,
  time: MomentInput,
  ignoreDateTz = true,
): Moment | null => {
  const dateMoment = ignoreDateTz ? moment.utc(date) : moment(date);
  const timeMoment = moment(time);

  const dateTimeMoment = timeMoment.clone();
  dateTimeMoment.tz(dateMoment?.tz() || moment.tz.guess());
  dateTimeMoment.set('year', dateMoment.year());
  dateTimeMoment.set('month', dateMoment.month());
  dateTimeMoment.set('date', dateMoment.date());

  if (dateTimeMoment.isValid()) {
    return dateTimeMoment;
  } else {
    throw new Error('Invalid datetime');
  }
};

export type JobForDeriveDate = Pick<Job, 'current_starts_time' | 'current_ends_time'> & {
  location?: Pick<
    NonNullable<Job['location']>,
    'timezone_lookup' | 'professional_can_adjust_times'
  > | null;
};

export const deriveDate = (
  job: JobForDeriveDate,
  time: Moment,
  mode: 'clock_in' | 'clock_out',
): string | null => {
  const startMoment = moment(job.current_starts_time).tz(
    job.location?.timezone_lookup || moment.tz.guess(),
  );
  const endMoment = moment(job.current_ends_time).tz(
    job.location?.timezone_lookup || moment.tz.guess(),
  );
  const currentMoment = moment(time).tz(job.location?.timezone_lookup || moment.tz.guess());

  if (startMoment.isAfter(endMoment)) {
    throw new Error('current_starts_time is after current_ends_time');
  } else if (endMoment.diff(startMoment, 'days') > 1) {
    throw new Error('current_starts_time and current_ends_time are more than one day apart');
  }

  const timeAtStartDate = setDateToTimeMoment(startMoment, time, false);
  const timeAtEndDate = setDateToTimeMoment(endMoment, time, false);

  if (mode === 'clock_out') {
    if (timeAtStartDate?.isBefore(startMoment, 'minute')) {
      return startMoment.clone().add(1, 'day').format('YYYY-MM-DD');
    }
  } else {
    if (timeAtEndDate?.isAfter(endMoment, 'minute')) {
      if (!job.location?.professional_can_adjust_times && currentMoment.isAfter(endMoment)) {
        return currentMoment.format('YYYY-MM-DD');
      }
      return endMoment.clone().subtract(1, 'day').format('YYYY-MM-DD');
    }
  }

  if (timeAtStartDate?.isBetween(startMoment, endMoment)) {
    return timeAtStartDate.format('YYYY-MM-DD');
  }
  if (timeAtEndDate?.isBetween(startMoment, endMoment)) {
    return timeAtEndDate.format('YYYY-MM-DD');
  }

  return mode === 'clock_in' ? startMoment.format('YYYY-MM-DD') : endMoment.format('YYYY-MM-DD');
};

export const resolveApprovedClockOutMoment = (
  job: JobForDeriveDate,
  value: MomentInput,
): Moment | null => {
  const timezone = job.location?.timezone_lookup || moment.tz.guess();
  const timeMoment = moment(value).tz(timezone);
  if (!value || !timeMoment.isValid()) {
    return null;
  }

  const startMoment = moment.tz(job.current_starts_time, timezone);
  const endMoment = moment.tz(job.current_ends_time, timezone);
  // Date derivation only applies to overnight shifts: on a same-day shift a time
  // before the start can never belong to the next day.
  if (startMoment.isSame(endMoment, 'date')) {
    return timeMoment;
  }

  try {
    const derivedDate = deriveDate(job, timeMoment, 'clock_out');
    if (!derivedDate) {
      return null;
    }
    return setDateToTimeMoment(moment.tz(derivedDate, 'YYYY-MM-DD', timezone), timeMoment, false);
  } catch {
    return null;
  }
};

export const normalizeApprovedClockOut = (job: JobForDeriveDate, value: string): string => {
  const resolved = resolveApprovedClockOutMoment(job, value);
  return resolved?.isValid() ? resolved.utc().toISOString() : value;
};

type JobForClockOutBounds = JobForDeriveDate & {
  approved_clock_in?: string | null;
  location?:
    | (NonNullable<JobForDeriveDate['location']> &
        Pick<NonNullable<Job['location']>, 'maximum_late_clock_out_grace_period_minutes'>)
    | null;
};

export type ApprovedClockOutBoundsResult = { valid: true } | { valid: false; message: string };

export const validateApprovedClockOutBounds = (
  job: JobForClockOutBounds,
  value: MomentInput,
): ApprovedClockOutBoundsResult => {
  const timezone = job.location?.timezone_lookup || moment.tz.guess();
  const clockOutMoment = resolveApprovedClockOutMoment(job, value);
  if (!clockOutMoment?.isValid()) {
    return { valid: false, message: 'Invalid clock out time' };
  }

  const clockInMoment = moment(job.approved_clock_in || job.current_starts_time).tz(timezone);
  if (clockInMoment.isValid() && !clockOutMoment.isAfter(clockInMoment)) {
    return {
      valid: false,
      message: `Clock out time must be after your clock in time (${clockInMoment.format('HH:mm')})`,
    };
  }

  const maxGraceMinutes = job.location?.maximum_late_clock_out_grace_period_minutes;
  const upperBound =
    typeof maxGraceMinutes === 'number'
      ? moment(job.current_ends_time).tz(timezone).add(maxGraceMinutes, 'minutes')
      : moment(job.current_ends_time).tz(timezone).add(24, 'hours');
  if (clockOutMoment.isAfter(upperBound)) {
    return {
      valid: false,
      message:
        'This time is past the maximum allowed end time. Please ask the facility admin to extend your shift.',
    };
  }

  return { valid: true };
};

export type JobForIsOvernight = Pick<Job, 'current_ends_time' | 'current_starts_time' | 'location'>;
export const isOvernight = (job: JobForIsOvernight): boolean =>
  !moment
    .tz(job.current_starts_time, job.location?.timezone_lookup || moment.tz.guess())
    .isSame(
      moment.tz(job.current_ends_time, job.location?.timezone_lookup || moment.tz.guess()),
      'date',
    );
